// ADS-AUTOPREVIEW 01 Schritt 3, Betreiberfreigabe 30.10.2026: Generierungsbegriff aus dem Ziel-Link.
// q (ausdrücklich) hat Vorrang; utm_term nur als Rückfall und nur mit fachlich lesbarem Inhalt.
const MAX_LAENGE = 120;
const PLATZHALTER = /[{}<>]/;
const TECHNISCH = /^(https?:|www\.)|^\(?(not set|not provided|none|null|undefined)\)?$|^((kwd|aud|dsa|pla)-\d+:?)+$/i;
const KENNUNG = /^(?=.*\d)[A-Za-z0-9_-]{20,}$/;
const BUCHSTABE = /\p{L}/u;

const kuerze = (t) => (t.length > MAX_LAENGE ? t.slice(0, MAX_LAENGE) : t);

function ausdruecklich(wert) {
  const t = typeof wert === "string" ? wert.trim() : "";
  return t && !PLATZHALTER.test(t) ? kuerze(t) : null;
}

function keyword(wert) {
  // Google-Ads-Schreibweisen [exakt], "Wortgruppe" und +modifiziert auf den reinen Begriff zurückführen.
  const t = (typeof wert === "string" ? wert : "")
    .trim()
    .replace(/^\[(.*)\]$/, "$1")
    .replace(/^"(.*)"$/, "$1")
    .replace(/(^|\s)\+(?=\S)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  if (t.length < 2 || PLATZHALTER.test(t) || TECHNISCH.test(t) || KENNUNG.test(t) || !BUCHSTABE.test(t)) {
    return null;
  }
  return kuerze(t);
}

export function generierungsbegriff(q, utmTerm) {
  return ausdruecklich(q) || keyword(utmTerm);
}

// ADS-SUCHBEGRIFF-01, Betreiberfreigabe 01.11.2026: Füllwörter (ganze Wörter, ohne Groß-/Kleinschreibung) entfernen.
// Ohne Füllwort bleibt der Begriff unverändert; bleibt kein Thema übrig → null (kurze Frage statt Einstufung).
const FUELLWOERTER = new Set([
  "gefährdungsbeurteilung", "gefährdungsbeurteilungen", "gefaehrdungsbeurteilung", "gefaehrdungsbeurteilungen", "gbu",
  "vorlage", "vorlagen", "muster", "beispiel", "pdf", "kostenlos", "gratis", "download", "word", "excel", "erstellen",
  "online", "software", "2025", "2026", "bgw", "bg", "für", "fuer",
]);
const RAND = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;

// ADS-ABSICHT-01, Betreiberfreigabe 01.11.2026: Hauptwörter werden immer gestrichen und tragen die Absicht der Suche.
// Nebenwörter: besteht der Rest nur aus ihnen → kein Thema; sonst bleibt der Rest unverändert (mit Nebenwörtern).
const absicht = (art, woerter) => woerter.map((w) => [w, art]);
const HAUPTWOERTER = new Map([
  ...absicht("unterweisung", ["unterweisung", "unterweisungen", "unterweisen", "sicherheitsunterweisung",
    "sicherheitsunterweisungen", "erstunterweisung", "unterweisungsnachweis", "unterweisungssoftware"]),
  ...absicht("betriebsanweisung", ["betriebsanweisung", "betriebsanweisungen"]),
  ...absicht("software", ["software", "arbeitsschutz", "arbeitssicherheit", "app", "programm", "tool", "e-learning",
    "arbeitsschutzmanagement", "ki"]),
]);
const VORRANG = ["betriebsanweisung", "unterweisung", "software"];
const NEBENWOERTER = new Set([
  "mitarbeiter", "arbeitgeber", "dokumentation", "digital", "digitale", "jährliche", "mehrsprachig", "automatisch",
  "lassen", "betriebe", "branche", "mit", "jeder",
]);
const E_LEARNING = /(^|\s)e\s+learning(?=\s|$)/giu; // „e learning“ (Bindestrich zu Leerraum) wie „e-learning“

function zerlege(begriff) {
  const roh = typeof begriff === "string" ? begriff.trim() : "";
  const woerter = roh.replace(E_LEARNING, "$1e-learning").split(/\s+/);
  return { roh, woerter, schluessel: woerter.map((w) => w.replace(RAND, "").toLowerCase()) };
}

export function themaAusSuchbegriff(begriff) {
  const { roh, woerter, schluessel } = zerlege(begriff);
  const rest = woerter.filter((_, i) => !FUELLWOERTER.has(schluessel[i]) && !HAUPTWOERTER.has(schluessel[i]));
  if (rest.every((w) => NEBENWOERTER.has(w.replace(RAND, "").toLowerCase()))) return null;
  const thema = rest.length === woerter.length ? roh : rest.join(" ");
  return thema.length >= 2 && BUCHSTABE.test(thema) ? thema : null;
}

// ADS-ABSICHT-01, Betreiberfreigabe 01.11.2026: Absicht aus den gestrichenen Hauptwörtern, sonst null.
export function absichtAusSuchbegriff(begriff) {
  const gefunden = new Set(zerlege(begriff).schluessel.map((s) => HAUPTWOERTER.get(s)));
  return VORRANG.find((a) => gefunden.has(a)) || null;
}

// Wiederaufnahme im Tab (sessionStorage wie herkunft.js): Neuladen desselben Links startet keine zweite Generierung.
const VORSCHAU_SCHLUESSEL = "clartext_lp3_vorschau";

function lese() {
  try {
    const wert = JSON.parse(window.sessionStorage.getItem(VORSCHAU_SCHLUESSEL));
    return wert && typeof wert === "object" ? wert : null;
  } catch {
    return null;
  }
}

function schreibe(wert) {
  try {
    window.sessionStorage.setItem(VORSCHAU_SCHLUESSEL, JSON.stringify(wert));
  } catch {
    /* blockierter Speicher: dann ohne Wiederaufnahme */
  }
}

export function gemerkteVorschau(begriff) {
  const w = lese();
  return w && w.begriff === begriff && typeof w.thema === "string" && typeof w.job_id === "string" ? w : null;
}

export function merkeVorschau(begriff, thema, jobId) {
  schreibe({ begriff, thema, job_id: jobId });
}

export function merkeErgebnis(jobId, result) {
  const w = lese();
  if (!w || w.job_id !== jobId || !result?.world_id || !result?.preview_session_token) return;
  schreibe({ ...w, world_id: result.world_id, preview_session_token: result.preview_session_token });
}

export function vergissVorschau() {
  try {
    window.sessionStorage.removeItem(VORSCHAU_SCHLUESSEL);
  } catch {
    /* ignore */
  }
}
